/**
 * Billing for the open workspace (core BillingViewModel, docs/features/billing.md): the BillingDeps (API, how to open
 * Stripe's pages, whether this client sells, where a banner snooze is kept), provided by the app shell next to
 * WorkspaceContext. Absent → no Billing page, no billing banners, composer and invite prompts as before. `useBilling`
 * shares one view model per workspace among Settings → Billing, the app chrome, the composer and the invite sheet.
 * Native: an `@Environment` value / `CompositionLocal` holding the same deps (with `sells: false`).
 */
import type { Member } from "@bundle/contracts";
import { BillingViewModel, type BillingDeps, type BillingScreenState } from "@bundle/core";
import { createContext, useCallback, useContext, useSyncExternalStore } from "react";
import { useOwned } from "../hooks/owned.ts";
import { WorkspaceContext } from "./AppContext.tsx";

/**
 * Deps for one workspace; `snooze` is created per workspace id by the shell. `contactPro` opens "Contact us" for Pro;
 * `takeCheckoutReturn` hands the Billing page the Stripe Checkout result this page load came back with, once.
 */
export type BillingContextValue = Omit<BillingDeps, "snooze" | "storageSnooze"> & {
  snooze?: (wsId: string) => BillingDeps["snooze"];
  storageSnooze?: (wsId: string) => BillingDeps["storageSnooze"];
  contactPro?: () => void;
  takeCheckoutReturn?: () => "success" | "canceled" | undefined;
};

/** The shell's billing deps (contact and Checkout return hooks included); null when it provides none. */
export function useBillingShell(): BillingContextValue | null {
  return useContext(BillingContext);
}

export const BillingContext = createContext<BillingContextValue | null>(null);

const noopSubscribe = () => () => {};
const none = (): null => null;
type Box = { vm: BillingViewModel | null; dispose: () => void };

/** The workspace's billing; null when the shell provides none. */
export function useBilling(): { vm: BillingViewModel; state: BillingScreenState } | null {
  const ctx = useContext(BillingContext);
  const workspace = useContext(WorkspaceContext)?.workspace;
  const deps = workspace === undefined ? null : ctx;
  const key = deps === null || workspace === undefined ? "billing:off" : `billing:${workspace.wsId}:${workspace.state.meId}`;
  const create = useCallback((): Box => {
    if (deps === null || workspace === undefined) return { vm: null, dispose: () => {} };
    const snooze = deps.snooze?.(workspace.wsId);
    const storageSnooze = deps.storageSnooze?.(workspace.wsId);
    const vm = new BillingViewModel(workspace, {
      api: deps.api,
      open: deps.open,
      sells: deps.sells,
      ...(snooze === undefined ? {} : { snooze }),
      ...(storageSnooze === undefined ? {} : { storageSnooze }),
    });
    return { vm, dispose: () => vm.dispose() };
  }, [deps, workspace]);
  const vm = useOwned(key, create).vm;
  const state = useSyncExternalStore(vm?.subscribe ?? noopSubscribe, vm?.getSnapshot ?? none, vm?.getSnapshot ?? none);
  return vm === null || state === null ? null : { vm, state };
}

/** Owners and admins by name (for "Ask Ana Souza or Bruno Lima to subscribe"), owners first, at most `max`. */
export function useBillingManagers(max = 3): Member[] {
  const cache = useContext(WorkspaceContext)?.workspace.state.cache;
  if (cache === undefined) return [];
  const rank = (m: Member) => (m.role === "owner" ? 0 : 1);
  return Object.values(cache.members)
    .filter((m) => (m.role === "owner" || m.role === "admin") && !m.deactivated && m.kind === "human")
    .sort((a, b) => rank(a) - rank(b) || a.displayName.localeCompare(b.displayName))
    .slice(0, max);
}
